import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	AggregateResultDto,
	ApiControllerRequest,
	ApiObjectDtoModel,
	CascadingLookupLoader,
	EditDto,
	extractJadeArrayReturn,
	JadeArrayReturn,
	LoadDto,
	LookupDto,
	LookupTypeDto,
	ScreenConfigDto,
	SoftFormDefInfoDto,
	SoftFormEditDto,
	Types,
	UseCaseController,
	UseCaseController_Constants,
} from '@siza-staff-care/shared/data';
import { EMPTY, map, Observable, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { JadeService } from './jade.service';
import { getCurrentSecurityMapping } from './menu-routes/root-route-to-security-mapping';
import { GenericDialogsService } from '@siza-staff-care/shared/generic-dialogs';

type CacheEntry = {
	result: LookupDto[];
	timeout$: Subject<void>;
};

const Default_Cache_Timeout = 10000; // 10 seconds

@Injectable({
	providedIn: 'root',
})
export class CrudService {
	private cache: Record<string, CacheEntry> = {};

	private router = inject(Router);
	private jade = inject(JadeService);
	private appState = inject(AppStateService);
	private dialogs = inject(GenericDialogsService);

	// TODO: Resolve usage of this - in old client, was using then refreshing a rota. (schedules.service.js:1711)
	setAbortNextRequest() {
		this.jade.setAbortNextRequest();
	}

	/**
	 * Runs the screen "open" function on the server's Jade controller, returning all required data
	 * to run the screen, including lookups and other config data.
	 * @param type The Jade controller class name.
	 * @param lookupTypes The lookup types to request.
	 * @param opts Additional options for opening the screen.
	 * @returns
	 */
	open<T extends ScreenConfigDto = ScreenConfigDto, TController = UseCaseController>(
		type: string,
		lookupTypes?: string[],
		opts?: TController | undefined,
	) {
		const types = lookupTypes ? lookupTypes.join(',') : '';

		const state = `ng:${this.getSecurityMappingFromRoute()}`;

		const params = this.buildParams(
			type,
			UseCaseController_Constants.Operation_Open,
			{
				state,
				lookupTypes: types,
			},
			opts ? (opts as Record<string, unknown>) : undefined,
		);

		return this.jade.post<T>(params).pipe(tap((result) => this.processLookups(result)));
	}

	/**
	 * Creates a new instance, calling the "create" operation on the Jade controller, and returning an EditDto.
	 * The CREATE part of CRUD.
	 * @param type
	 * @param data
	 * @returns
	 */
	create<T extends ApiObjectDtoModel = AggregateResultDto>(type: string, data?: Record<string, unknown>) {
		return this.jade.controllerRequest<T>(UseCaseController_Constants.Operation_Create, {
			$type: type,
			...data,
		});
	}

	/**
	 * Selects an instance for editing. The READ part of CRUD.
	 * @param type
	 * @param oid
	 * @returns
	 */
	select<T extends ApiObjectDtoModel = AggregateResultDto>(
		type: string,
		oid?: string,
		data?: Record<string, unknown>,
	) {
		return this.jade.controllerRequest<T>(UseCaseController_Constants.Operation_Select, {
			$type: type,
			oid,
			...data,
		});
	}

	/**
	 * Saves changes to an instance. The UPDATE part of CRUD.
	 * @param type
	 * @param edition
	 * @param oid
	 * @param model
	 * @param opts
	 * @returns
	 */
	save<T extends EditDto>(
		type: string,
		edition: number,
		oid: string,
		model: T,
		opts?: Record<string, unknown>,
	): Observable<AggregateResultDto> {
		return this.jade.controllerRequest<AggregateResultDto>(UseCaseController_Constants.Operation_Save, {
			$type: type,
			ed: edition,
			oid,
			model,
			...opts,
		});
	}

	/**
	 * Deletes an instance. The DELETE part of CRUD.
	 * @param type
	 * @param oid
	 * @param opts
	 * @returns
	 */
	delete<T extends ApiObjectDtoModel = AggregateResultDto>(
		type: string,
		oid: string,
		opts?: Record<string, unknown>,
	) {
		return this.jade.controllerRequest<T>(UseCaseController_Constants.Operation_Delete, {
			$type: type,
			oid,
			...opts,
		});
	}

	/**
	 * Performs a Load operation, for a related OID, returning a LoadDto.
	 * @param type
	 * @param oid
	 * @param opts
	 * @returns
	 */
	load<T extends LoadDto = LoadDto>(type: string, oid: string, opts?: Record<string, unknown>) {
		return this.jade
			.controllerRequest<AggregateResultDto>(UseCaseController_Constants.Operation_Load, {
				$type: type,
				oid,
				...opts,
			})
			.pipe(
				map((result) => {
					return result.dtos[0] as T;
				}),
				tap((result) => {
					if (opts?.['processLookups']) {
						this.processLookups(result);
					}
				}),
			);
	}

	/**
	 * Executes a miscellaneous operation on the Jade controller.
	 * @param type
	 * @param operation
	 * @param params
	 * @param opts
	 * @returns
	 */
	other<T>(type: string, operation: string, params?: Record<string, unknown>, opts?: { processLookups?: boolean }) {
		return this.jade
			.controllerRequest<T>(operation, {
				$type: type,
				...params,
			})
			.pipe(
				switchMap((response) => {
					if (response?.['warnings'] && Array.isArray(response['warnings']) && response['warnings'].length) {
						const warnings = response['warnings'].join('\n');
						return this.dialogs
							.confirm({
								title: 'Warning',
								message: warnings,
								customConfirmCaption: 'Confirm',
								maxWidth: '100ch',
							})
							.pipe(
								switchMap((confirmed) => {
									if (confirmed) {
										const rtn: Observable<T> = this.other<T>(
											type,
											operation,
											{
												...params,
												confirmWarning: true,
											},
											opts,
										);
										return rtn;
									} else {
										return EMPTY;
									}
								}),
							);
					}

					return of(response);
				}),
				tap((result) => {
					if (opts?.processLookups) {
						this.processLookups(result);
					}
				}),
			);
	}

	execute<T>(type: string, params?: Record<string, unknown>) {
		return this.jade
			.controllerRequest<T>('execute', {
				$type: type,
				...params,
			})
			.pipe(
				tap((result) => {
					this.processLookups(result);
				}),
			);
	}

	/**
	 * Stores a cascading lookup result in cache to avoid a server request if it can be satisfied
	 * directly.
	 * @param {string} oid
	 * @param {string} typeName
	 * @param {string} collectionPropertyName
	 * @param {*} result
	 */
	cacheCascadingLookup<TWrapper extends LookupDto>(
		oid: string,
		typeName: string,
		collectionPropertyName: string,
		result: TWrapper[],
		cacheTimeout?: number,
	) {
		cacheTimeout = cacheTimeout || Default_Cache_Timeout;

		const key = this.buildCacheKey(oid, typeName, collectionPropertyName);
		let cached = this.cache[key];

		// If the cache entry already exists, cancel its timeout.
		if (cached) {
			cached.timeout$.next();
			cached.timeout$.complete();
		}

		// Build the new cache entry.
		cached = {
			result,
			timeout$: new Subject<void>(),
		};

		timer(cacheTimeout)
			.pipe(
				tap(() => {
					cached.timeout$.next();
					cached.timeout$.complete();

					delete this.cache[key];
				}),
				takeUntil(cached.timeout$),
			)
			.subscribe();

		// Record the entry in the cache.
		this.cache[key] = cached;
	}

	/**
	 * Uses the CascadingLookupLoader Jade controller to load a cascading lookup from an object ID and collection name.
	 * The OID is used to find that object, then the collection name is used to identify a collection to iterate and return
	 * DTOs for the typeName class.
	 * @param opts
	 * @returns
	 */
	loadCascadingLookup<TWrapper extends LookupDto>(opts: {
		oid: string;
		typeName: string;
		collectionPropertyName: string;
		store?: Record<string, TWrapper[] | null>;
		key?: string;
	}): Observable<TWrapper[]> {
		if (!opts.oid) {
			if (opts.store && opts.key) {
				opts.store[opts.key] = null;
			}

			return of([]);
		}

		// Try the cache first
		const cacheKey = this.buildCacheKey(opts.oid, opts.typeName, opts.collectionPropertyName);
		const cached = this.cache[cacheKey];
		if (cached) {
			// Cache hit - resolve immediately.
			if (opts.store && opts.key) {
				opts.store[opts.key] = cached.result as TWrapper[];
			}

			return of(cached.result as TWrapper[]);
		}

		const loaderParams: CascadingLookupLoader = {
			$type: Types.CascadingLookupLoader,
			typeName: opts.typeName,
			oid: opts.oid,
			collectionPropertyName: opts.collectionPropertyName,
		};

		return this.jade
			.post<JadeArrayReturn<TWrapper>>({
				$type: Types.ApiControllerRequest,
				params: loaderParams,
			})
			.pipe(
				map((result) => extractJadeArrayReturn<TWrapper>(result)),
				tap((result) => {
					if (opts.store && opts.key) {
						opts.store[opts.key] = result;
					}
				}),
			);
	}

	/**
	 * Gets the soft form definition and a blank soft form object for a related object
	 * i.e. an Event, Training, SkillCompetency, or StaffExtra. The oid is for the
	 * Event, etc.
	 * OR, the oid can be the SoftFormDef oid.
	 *
	 * Will return empty definition (i.e. with a null oid) and soft form objects if
	 * there is no associated SoftFormDef.
	 *
	 * @param  {String} oid The oid of the SoftFormDef or of the related object (e.g. Event)
	 */
	getSoftFormDef(oid: string) {
		const params = {
			$type: Types.ApiControllerRequest,
			params: {
				$type: Types.SoftFormDefLoader,
				oid,
			},
		};

		return this.jade.post<AggregateResultDto>(params).pipe(
			map((result) => {
				const softFormDef = result.dtos[0] as SoftFormDefInfoDto;
				const softForEditDto = result.dtos[1] as SoftFormEditDto;

				return {
					softFormDef,
					softForEditDto,
				};
			}),
		);
	}

	/**
	 * Builds a cache key for the parameters by concatenating the strings.
	 * @param {string} oid
	 * @param {string} typeName
	 * @param {string} collectionPropertyName
	 * @returns {string}
	 */

	private buildCacheKey(oid: string, typeName: string, collectionPropertyName: string): string {
		return `${oid}_${typeName}_${collectionPropertyName}`;
	}

	/**
	 * Looks for lookups properties on the result object, and if
	 * found, registers them into app state.
	 * @param result
	 * @returns
	 */
	private processLookups(result) {
		if (result && result['lookups']) {
			const lookups: LookupTypeDto[] = extractJadeArrayReturn<LookupTypeDto>(result['lookups']);
			this.appState.registerLookups(lookups);

			// Remove the lookups property from the result.
			delete result['lookups'];
		}

		// If there's an array of DTOs, check for lookups properties on each dto.
		if (result && result.dtos && Array.isArray(result.dtos)) {
			result.dtos.forEach((dto) => {
				if (dto && dto['lookups']) {
					const lookups: LookupTypeDto[] = extractJadeArrayReturn<LookupTypeDto>(dto['lookups']);
					this.appState.registerLookups(lookups);
				}
			});
		}
		return result;
	}

	/**
	 * Builds a parameters object for sending a request to Jade.
	 * @param  {string} controllerClass - The name of the screen's controller class.
	 * @param  {string} operation - The operation
	 * @param  {object} parameters - The parameters
	 * @param  {object} opts - Additional parameters
	 * @returns {object} The parameters object.
	 */
	private buildParams(
		controllerClass: string,
		operation: string,
		parameters?: Record<string, unknown>,
		opts?: Record<string, unknown>,
	): object {
		let params: Partial<ApiControllerRequest> = {
			$type: Types.ApiControllerRequest,
		};

		let opParams = {
			$type: controllerClass,
			operation,
		};

		opParams = {
			...opParams,
			...parameters,
			...opts,
		};

		params = {
			...params,
			params: {
				...opParams,
			},
		};

		return params;
	}

	/**
	 * Gets the securityMappings value for the current route. E.g. expenses-query would return ExpensesQuery, which would be
	 * equivalent to securityMappings.ExpensesQuery ( == 'ExpensesQuery' )
	 * @returns
	 */
	private getSecurityMappingFromRoute(): string {
		return getCurrentSecurityMapping(this.router, this.appState) ?? '';
	}
}
